import React from 'react';
import { Button } from "@/components/ui/button";
import { Zap, LogOut, User, ChevronDown, BadgeIndianRupee, TrendingUp } from "lucide-react";
import { useAuth } from '@/hooks/useAuth';
import { Link, useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogoClick = () => {
    if (user) {
      const element = document.getElementById('dashboard-top');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/dashboard');
      }
    } else {
      const element = document.getElementById('hero-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
      }
    }
  };

  const handleTop5NewsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Force navigation to top-5-news route
    navigate('/top-5-news');
  };

  return (
    <nav className="fixed w-full top-0 z-50 py-5 px-4 md:px-6 backdrop-blur-md bg-black/90 border-b border-[#9E7C47]/30">
      <div className="container mx-auto flex justify-between items-center">
        <button 
          onClick={handleLogoClick}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <img src="/coin_logo.png" alt="Wealth Manager" className="h-8 w-8" />
          <span className="font-bold text-xl tracking-tight text-white">Wealth Manager</span>
        </button>
        
        <div className="flex items-center gap-4">
          {user ? (
            <>
              <div className="hidden md:flex items-center gap-4">
                <Link to="/dashboard" className="text-sm text-white/70 hover:text-white">Dashboard</Link>
                <button 
                  onClick={handleTop5NewsClick}
                  className="text-sm text-white/70 hover:text-white flex items-center"
                >
                  <TrendingUp className="h-3.5 w-3.5 mr-1" />
                  Top 5 News
                </button>
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-[#9E7C47] text-[#9E7C47] hover:bg-[#9E7C47] hover:text-black"
                  >
                    <User className="h-4 w-4 mr-1" /> 
                    {user.email && user.email.split('@')[0]} 
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-black border-[#9E7C47]/30">
                  <DropdownMenuLabel className="text-white/70">
                    <span className="block text-xs text-white/50">Signed in as</span>
                    <span className="block truncate text-[#F9F1A9]">{user.email}</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuGroup>
                    <DropdownMenuItem className="text-white/70 hover:text-white focus:text-white focus:bg-[#9E7C47]/20">
                      <Link to="/dashboard" className="flex items-center w-full">Dashboard</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-white/70 hover:text-white focus:text-white focus:bg-[#9E7C47]/20">
                      <Link to="/profile-setup" className="flex items-center w-full">Investor Profile</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-white/70 hover:text-white focus:text-white focus:bg-[#9E7C47]/20">
                      <Link to="/top-5-news" className="flex items-center w-full">
                        <TrendingUp className="h-4 w-4 mr-2" /> Market News
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-white/70 hover:text-white focus:text-white focus:bg-[#9E7C47]/20">
                      <Link to="/credits-referrals" className="flex items-center w-full">
                        <BadgeIndianRupee className="h-4 w-4 mr-2" /> Credits & Referrals
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    className="text-white/70 hover:text-white focus:text-white focus:bg-[#9E7C47]/20"
                    onClick={() => signOut()}
                  >
                    <LogOut className="h-4 w-4 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <button 
                onClick={handleTop5NewsClick}
                className="text-sm text-white/70 hover:text-white flex items-center"
              >
                <TrendingUp className="h-3.5 w-3.5 mr-1" />
                Top 5 News
              </button>
              {/* <Link to="/waitlist" className="text-sm text-white/70 hover:text-white hidden md:block">Join Waitlist</Link> */}
              <a href="/coming-soon" className="text-sm text-white/70 hover:text-white hidden md:block">Coming Soon</a>
              <Link to="/waitlist" className="hidden md:block">
                <Button size="sm" className="bg-gradient-to-r from-[#9E7C47] via-[#F9F1A9] to-[#9E7C47] hover:from-[#8A6B3A] hover:via-[#E8E090] hover:to-[#8A6B3A] text-black font-bold shadow-lg hover:shadow-xl hover:shadow-[#9E7C47]/30 transition-all duration-300 hover:scale-105 border-0">
                  Join Waitlist
                </Button>
              </Link>
              <Link to="/waitlist" className="md:hidden">
                <Button size="sm" className="bg-gradient-to-r from-[#9E7C47] via-[#F9F1A9] to-[#9E7C47] hover:from-[#8A6B3A] hover:via-[#E8E090] hover:to-[#8A6B3A] text-black font-bold shadow-lg hover:shadow-xl hover:shadow-[#9E7C47]/30 transition-all duration-300 hover:scale-105 border-0">
                  <Zap className="h-4 w-4 mr-1" /> Join
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
